import { useNavigate } from "react-router-dom";
import { Type } from "@themes/pollinator/ui/Type";
import { Dialog } from "@themes/pollinator/ui/Dialog";

export type Props = {
  errorMessage: string;
  /** Header text. Defaults to "Access Denied" so unstructured fetch / verify errors render unchanged. */
  title?: string;
  /**
   * Optional bulleted remediation steps. Two or more renders as a `<ul>`; one
   * renders inline as additional prose; none preserves the existing
   * single-line error message.
   */
  causes?: string[];
  /**
   * Legacy mode — fetch / access-verify errors. When `onRetry` is provided,
   * primary action is "Try Again". Secondary action is always
   * "Back to Dashboard". X / Escape / backdrop also navigate to the
   * dashboard.
   *
   * Mutually exclusive with the device-error mode props (`onContinue` /
   * `onCancel`). If both modes' props are supplied, device-error mode wins.
   */
  onRetry?: () => void;
  /**
   * Device-error mode — replaces the legacy actions with
   * "Continue to Meeting" (primary) and "Cancel" (secondary). X / Escape /
   * backdrop call `onCancel` so dismissing the dialog returns to the join
   * page rather than navigating to the dashboard. Used by the device-start
   * failure path: the SDK is already joined, so Continue just navigates,
   * Cancel tears down the half-joined session.
   */
  onContinue?: () => void;
  onCancel?: () => void;
};

/**
 * Modal dialog for video-join failures — fetch errors, room-access denial,
 * and device-start failures. Wraps the themed `Dialog` so the error appears
 * over whatever page rendered it.
 *
 * Two action modes:
 *   - Legacy (default): "Try Again" + "Back to Dashboard". Used by fetch
 *     and access-verify call sites.
 *   - Device-error: "Continue to Meeting" + "Cancel". Used by the
 *     device-start failure path so the user can choose to proceed despite
 *     missing camera/mic, or cancel back to the join page to fix devices.
 */
export function VideoMeetingAccessError({
  errorMessage,
  title,
  causes,
  onRetry,
  onContinue,
  onCancel,
}: Props) {
  const navigate = useNavigate();
  const renderListCauses = !!causes && causes.length >= 2;
  const renderInlineCause = !!causes && causes.length === 1;
  const goToDashboard = () => navigate("/dashboard");
  const isDeviceErrorMode = !!onContinue && !!onCancel;

  const primaryButtonText = isDeviceErrorMode
    ? "Continue to Meeting"
    : onRetry
    ? "Try Again"
    : undefined;
  const onPrimaryClick = isDeviceErrorMode ? onContinue : onRetry;

  const secondaryButtonText = isDeviceErrorMode ? "Cancel" : "Back to Dashboard";
  const onSecondaryClick = isDeviceErrorMode ? onCancel : goToDashboard;

  const onClose = isDeviceErrorMode ? onCancel! : goToDashboard;

  return (
    <Dialog
      isOpen={true}
      onClose={onClose}
      title={title ?? "Access Denied"}
      description={errorMessage}
      size="md"
      primaryButtonText={primaryButtonText}
      onPrimaryClick={onPrimaryClick}
      secondaryButtonText={secondaryButtonText}
      onSecondaryClick={onSecondaryClick}
    >
      {renderInlineCause && (
        <Type variant="body2">{causes![0]}</Type>
      )}
      {renderListCauses && (
        <ul className="list-disc list-outside pl-5 flex flex-col gap-2">
          {causes!.map((cause, i) => (
            <li key={i}>
              <Type variant="body2">{cause}</Type>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
